import axios from './axios';

export interface Device {
  _id: string;
  name: string;
  token: string;
  location: {
    country: {
      code: string;
      name: string;
    };
    city: {
      stateCode: string;
      name: string;
    };
    district: {
      name: string;
      coordinates: {
        latitude: number;
        longitude: number;
      };
    };
    address: string;
  };
  company: {
    _id: string;
    name: string;
  };
  groupData?: {
    _id: string;
    name: string;
  };
  isOnline: boolean;
  lastSeen?: Date;
  activePlaylist?: {
    _id: string;
    name: string;
  };
  deviceInfo?: {
    manufacturer?: string;
    model?: string;
    osVersion?: string;
    appVersion?: string;
  };
  scheduledCacheClear?: {
    scheduledAt: Date;
    createdAt: Date;
  };
  cacheCleanupInterval?: 'off' | '6h' | '12h' | '24h';
  lastAutoCacheCleanup?: Date;
  hasCertificate?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateDeviceDto {
  name: string;
  token: string;
  location: {
    country: {
      code: string;
      name: string;
    };
    city: string;
    district: string;
    address: string;
  };
  company: string;
  groupId?: string;
}

export interface UpdateDeviceDto {
  name?: string;
  location?: {
    country?: {
      code?: string;
      name?: string;
    };
    city?: string;
    district?: string;
    address?: string;
  };
  groupId?: string;
}

export const deviceService = {
  // Tüm cihazları getir
  async getAllDevices(page: number = 1, limit: number = 50) {
    try {
      const response = await axios.get('/devices', {
        params: { page, limit }
      });
      // Backend'den { devices: [], pagination: {} } formatında gelir
      return response.data;
    } catch (error) {
      console.error('Cihazlar alınırken hata:', error);
      throw error;
    }
  },

  // Yeni cihaz oluştur
  async createDevice(deviceData: CreateDeviceDto) {
    try {
      const response = await axios.post('/devices', deviceData);
      return response.data;
    } catch (error) {
      console.error('Cihaz oluşturulurken hata:', error);
      throw error;
    }
  },

  // Cihaz sil
  async deleteDevice(deviceId: string) {
    try {
      const response = await axios.delete(`/devices/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error('Cihaz silinirken hata:', error);
      throw error;
    }
  },

  // Cihazı yeniden başlat
  async restartDevice(deviceId: string) {
    try {
      const response = await axios.post(`/devices/${deviceId}/restart`);
      return response.data;
    } catch (error) {
      console.error('Cihaz yeniden başlatılırken hata:', error);
      throw error;
    }
  },

  // Cihaz ses seviyesini ayarla
  async setVolume(deviceId: string, volume: number) {
    try {
      const response = await axios.post(`/devices/${deviceId}/volume`, { volume });
      return response.data;
    } catch (error) {
      console.error('Ses seviyesi ayarlanırken hata:', error);
      throw error;
    }
  },

  // Toplu ses seviyesi güncelle
  async bulkUpdateVolume(deviceIds: string[], volume: number) {
    try {
      const response = await axios.put('/devices/bulk/volume', { deviceIds, volume });
      return response.data;
    } catch (error) {
      console.error('Toplu ses seviyesi güncellenirken hata:', error);
      throw error;
    }
  },

  // Acil durdurma
  async emergencyStop(deviceIds?: string[]) {
    try {
      const response = await axios.post('/devices/emergency-stop', { deviceIds });
      return response.data;
    } catch (error) {
      console.error('Acil durdurma sırasında hata:', error);
      throw error;
    }
  },

  // Acil sıfırlama
  async emergencyReset(deviceIds?: string[]) {
    try {
      const response = await axios.post('/devices/emergency-reset', { deviceIds });
      return response.data;
    } catch (error) {
      console.error('Acil sıfırlama sırasında hata:', error);
      throw error;
    }
  },

  // Cache temizleme (anlık veya zamanlanmış)
  async clearCache(deviceId: string, scheduledAt?: Date) {
    try {
      const response = await axios.post(`/devices/${deviceId}/clear-cache`, {
        scheduledAt: scheduledAt?.toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('Cache temizleme sırasında hata:', error);
      throw error;
    }
  },

  // Zamanlanmış cache temizlemeyi iptal et
  async cancelScheduledCacheClear(deviceId: string) {
    try {
      const response = await axios.delete(`/devices/${deviceId}/clear-cache/scheduled`);
      return response.data;
    } catch (error) {
      console.error('Zamanlanmış cache temizleme iptal edilirken hata:', error);
      throw error;
    }
  },

  // Cache ayarlarını güncelle (otomatik temizleme interval)
  async updateCacheSettings(deviceId: string, cacheCleanupInterval: 'off' | '6h' | '12h' | '24h') {
    try {
      const response = await axios.patch(`/devices/${deviceId}/cache-settings`, {
        cacheCleanupInterval
      });
      return response.data;
    } catch (error) {
      console.error('Cache ayarları güncellenirken hata:', error);
      throw error;
    }
  },

  // Müzik zamanlama oluştur
  async scheduleMusicPlayback(deviceId: string, schedule: {
    startDateTime: string;
    endDateTime: string;
    days: string[];
  }) {
    try {
      const response = await axios.post(`/devices/${deviceId}/music-schedule`, schedule);
      return response.data;
    } catch (error) {
      console.error('Müzik zamanlama oluşturulurken hata:', error);
      throw error;
    }
  }
};
